import { isPlainObject } from 'lodash-es'

const CHAR_UPPER_A = 0x41
const CHAR_LOWER_A = 0x61
const CHAR_UPPER_Z = 0x5a
const CHAR_LOWER_Z = 0x7a
const CHAR_0 = 0x30
const CHAR_9 = 0x39
const CHAR_MINUS = 0x2d
const CHAR_SPACE = 0x20
const CHAR_UNDERSCORE = 0x5f

type ObjectOrArray = Record<string, unknown> | Array<unknown>

const isUpper = (c: number) => CHAR_UPPER_A <= c && c <= CHAR_UPPER_Z
const isLower = (c: number) => CHAR_LOWER_A <= c && c <= CHAR_LOWER_Z
const isDigit = (c: number) => CHAR_0 <= c && c <= CHAR_9
const toUpper = (c: number) => c - 0x20
const toLower = (c: number) => c + 0x20

const isObjectOrArray = (value: unknown): value is ObjectOrArray => isPlainObject(value) || Array.isArray(value)

export const camelize = (str: string) => {
  const firstChar = str.charCodeAt(0)

  if (isDigit(firstChar) || firstChar === CHAR_MINUS || isNaN(firstChar)) {
    return str
  }

  let changed = isUpper(firstChar)
  const transformed = changed ? [toLower(firstChar)] : [firstChar]

  const length = str.length
  for (let i = 1; i < length; i++) {
    let c = str.charCodeAt(i)

    if (c === CHAR_UNDERSCORE || c === CHAR_SPACE || c === CHAR_MINUS) {
      changed = true
      c = str.charCodeAt(++i)

      if (isNaN(c)) {
        return str
      }

      if (isLower(c)) {
        transformed.push(toUpper(c))
      } else {
        transformed.push(c)
      }
    } else {
      transformed.push(c)
    }
  }

  if (!changed) {
    return str
  }

  return String.fromCharCode.apply(undefined, transformed)
}

export const decamelize = (str: string) => {
  const firstChar = str.charCodeAt(0)

  if (!isLower(firstChar) || isNaN(firstChar)) {
    return str
  }

  let changed = false
  const transformed = [firstChar]

  const length = str.length
  for (let i = 1; i < length; i++) {
    const c = str.charCodeAt(i)

    if (isUpper(c)) {
      changed = true
      transformed.push(CHAR_UNDERSCORE)
      transformed.push(toLower(c))
    } else {
      transformed.push(c)
    }
  }

  if (!changed) {
    return str
  }

  return String.fromCharCode.apply(undefined, transformed)
}

export const pascalize = (str: string) => {
  const firstChar = str.charCodeAt(0)

  if (isDigit(firstChar) || firstChar === CHAR_MINUS || isNaN(firstChar)) {
    return str
  }

  let changed = isLower(firstChar)
  const transformed = changed ? [toUpper(firstChar)] : [firstChar]

  const len = str.length
  for (let i = 1; i < len; i++) {
    let c = str.charCodeAt(i)

    if (c === CHAR_UNDERSCORE || c === CHAR_SPACE || c === CHAR_MINUS) {
      changed = true
      c = str.charCodeAt(++i)

      if (isNaN(c)) {
        return str
      }

      if (isLower(c)) {
        transformed.push(toUpper(c))
      } else {
        transformed.push(c)
      }
    } else {
      transformed.push(c)
    }
  }

  if (!changed) {
    return str
  }

  return String.fromCharCode.apply(undefined, transformed)
}

export const depascalize = (str: string) => {
  const firstChar = str.charCodeAt(0)

  if (isNaN(firstChar)) return str

  const transformed = [isUpper(firstChar) ? toLower(firstChar) : firstChar]

  const length = str.length
  for (let i = 1; i < length; i++) {
    const c = str.charCodeAt(i)

    if (isUpper(c)) {
      transformed.push(CHAR_UNDERSCORE)
      transformed.push(toLower(c))
    } else {
      transformed.push(c)
    }
  }

  return String.fromCharCode.apply(undefined, transformed)
}

const transformArray = (array: Array<unknown>, transform: (key: string) => string) => {
  const length = array.length
  const transformed = new Array(length)
  let idx = 0
  for (const item of array) {
    transformed[idx++] = isObjectOrArray(item) ? transformKeys(item, transform) : item
  }
  return transformed
}

const transformKeys = (obj: ObjectOrArray | Array<unknown>, transform: (key: string) => string) => {
  if (Array.isArray(obj)) {
    return transformArray(obj, transform)
  }

  if (typeof obj.prototype !== 'undefined') {
    return obj
  }

  const transformed: Record<string, unknown> = {}
  for (const key in obj) {
    const value = obj[key]
    const nextKey = transform(key)
    transformed[nextKey] = isObjectOrArray(value) ? transformKeys(value, transform) : value
  }
  return transformed
}

const transformArrayInPlace = (array: Array<unknown>, transform: (key: string) => string) => {
  let idx = 0
  for (const item of array) {
    array[idx++] = isObjectOrArray(item) ? transformKeysInPlace(item, transform) : item
  }
  return array
}

const transformKeysInPlace = (obj: ObjectOrArray, transform: (key: string) => string) => {
  if (Array.isArray(obj)) {
    return transformArrayInPlace(obj, transform)
  }

  for (const key in obj) {
    const value = obj[key]
    const nextKey = transform(key)

    if (nextKey !== key) {
      delete obj[key]
    }

    obj[nextKey] = isObjectOrArray(value) ? transformKeysInPlace(value, transform) : value
  }

  return obj
}

export const camelizeKeys = (obj: ObjectOrArray) => (isObjectOrArray(obj) ? transformKeys(obj, camelize) : obj)
export const decamelizeKeys = (obj: ObjectOrArray) => (isObjectOrArray(obj) ? transformKeys(obj, decamelize) : obj)
export const pascalizeKeys = (obj: ObjectOrArray) => (isObjectOrArray(obj) ? transformKeys(obj, pascalize) : obj)
export const depascalizeKeys = (obj: ObjectOrArray) => (isObjectOrArray(obj) ? transformKeys(obj, depascalize) : obj)

export const camelizeKeysInPlace = (obj: ObjectOrArray) =>
  isObjectOrArray(obj) ? transformKeysInPlace(obj, camelize) : obj
export const decamelizeKeysInPlace = (obj: ObjectOrArray) =>
  isObjectOrArray(obj) ? transformKeysInPlace(obj, decamelize) : obj
export const pascalizeKeysInPlace = (obj: ObjectOrArray) =>
  isObjectOrArray(obj) ? transformKeysInPlace(obj, pascalize) : obj
export const depascalizeKeysInPlace = (obj: ObjectOrArray) =>
  isObjectOrArray(obj) ? transformKeysInPlace(obj, depascalize) : obj
